/**
 * FundsPage — restricted money, kept visibly separate.
 *
 * A gift for the building and a gift for the electricity bill arrive at the
 * same counter and land in the same bank account. Nothing in a chart of
 * accounts distinguishes them, so the only thing standing between a restricted
 * gift and the wrong invoice was somebody remembering.
 *
 * The figure this screen exists for is the one at the top: what may actually be
 * spent freely. A bank balance looks healthy right up to the moment the
 * building fund asks for its money, which is why the restricted total is
 * subtracted from it here rather than reported beside it.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Loader2, ShieldCheck, AlertTriangle, Lock, Unlock, ArrowRight, ArrowLeft, PiggyBank, Plus,
} from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BASE = '/api/finance/funds';

const call = async (path, options = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.detail || 'Request failed');
  return body;
};

const money = (value) => Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const RESTRICTION_COPY = {
  restricted: {
    label: 'Restricted by the giver',
    tone: 'border-rose-200 bg-rose-50 text-rose-700',
    note: 'The giver set this purpose. It cannot be spent elsewhere, and the trustees cannot release it.',
  },
  designated: {
    label: 'Designated by the trustees',
    tone: 'border-amber-200 bg-amber-50 text-amber-700',
    note: 'The trustees set this purpose and can lift it by resolution.',
  },
  unrestricted: {
    label: 'Unrestricted',
    tone: 'border-slate-200 bg-slate-50 text-slate-600',
    note: 'Reported as a fund for clarity, but free to spend.',
  },
};

const OpenFundDialog = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    code: '', name: '', restriction: 'restricted', purpose: '', categories: '',
  });

  const create = useMutation({
    mutationFn: () => call(BASE, {
      method: 'POST',
      body: JSON.stringify({
        code: form.code.trim().toLowerCase(),
        name: form.name.trim(),
        restriction: form.restriction,
        purpose: form.purpose.trim(),
        categories: form.categories.split(',').map((c) => c.trim()).filter(Boolean),
      }),
    }),
    onSuccess: () => {
      toast.success(`${form.name} opened`, {
        description: 'Gifts in its categories will be directed to it from now on.',
      });
      queryClient.invalidateQueries({ queryKey: ['funds'] });
      setForm({ code: '', name: '', restriction: 'restricted', purpose: '', categories: '' });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="open-fund-dialog">
        <DialogHeader><DialogTitle>Open a fund</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="fund-code" className="text-xs">Code</Label>
              <Input id="fund-code" placeholder="building" value={form.code}
                     onChange={(e) => setForm({ ...form, code: e.target.value })}
                     data-testid="fund-code-input" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="fund-name" className="text-xs">Name</Label>
              <Input id="fund-name" placeholder="Building Fund" value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="fund-name-input" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Who set the restriction</Label>
            <Select value={form.restriction}
                    onValueChange={(v) => setForm({ ...form, restriction: v })}>
              <SelectTrigger data-testid="fund-restriction-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="restricted">The giver — cannot be released</SelectItem>
                <SelectItem value="designated">The trustees — can be released</SelectItem>
                <SelectItem value="unrestricted">Nobody — free to spend</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              {RESTRICTION_COPY[form.restriction]?.note}
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="fund-purpose" className="text-xs">Purpose</Label>
            <Textarea id="fund-purpose" rows={2} value={form.purpose}
                      placeholder="Construction and renovation of the sanctuary"
                      onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                      data-testid="fund-purpose-input" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="fund-categories" className="text-xs">
              Gift categories directed here
            </Label>
            <Input id="fund-categories" placeholder="building_fund, building"
                   value={form.categories}
                   onChange={(e) => setForm({ ...form, categories: e.target.value })}
                   data-testid="fund-categories-input" />
            <p className="text-xs text-slate-500">
              Comma separated. A category can belong to only one fund — otherwise a gift
              has two homes and the system has to guess which.
            </p>
          </div>
          <Button className="w-full" disabled={!form.code || !form.name || create.isPending}
                  onClick={() => create.mutate()} data-testid="fund-submit-btn">
            {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Open the fund'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const FundDrillDown = ({ code, onClose }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['fund-entries', code],
    queryFn: () => call(`${BASE}/${code}/entries`),
    enabled: !!code,
  });

  return (
    <Dialog open={!!code} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="fund-drilldown">
        <DialogHeader>
          <DialogTitle>{data?.name || code}</DialogTitle>
        </DialogHeader>
        {isLoading && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
        {data && (
          <>
            <p className="text-sm text-slate-600">
              Holding <span className="font-mono font-medium">{money(data.balance)}</span>
              {data.purpose ? ` for ${data.purpose}` : ''}
            </p>
            <div className="max-h-[55vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white text-left text-[11px] uppercase tracking-wider text-slate-400">
                  <tr>
                    <th className="py-2">Date</th>
                    <th>What happened</th>
                    <th>Came from</th>
                    <th className="text-right">In</th>
                    <th className="text-right">Out</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((e) => (
                    <tr key={`${e.entry_id}-${e.movement}`} className="border-t border-slate-100"
                        data-testid={`fund-entry-${e.entry_id}`}>
                      <td className="py-2 font-mono text-xs">{e.date}</td>
                      <td className="max-w-xs truncate">{e.description}</td>
                      <td className="text-xs text-slate-500">
                        {e.source_type ? String(e.source_type).replace(/_/g, ' ') : 'entered by hand'}
                      </td>
                      <td className="text-right font-mono text-xs text-emerald-700">
                        {e.received ? money(e.received) : ''}
                      </td>
                      <td className="text-right font-mono text-xs text-rose-700">
                        {e.spent ? money(e.spent) : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.entries.length === 0 && (
                <p className="py-10 text-center text-sm text-slate-500" data-testid="fund-drilldown-empty">
                  Nothing has been received into or paid out of this fund yet.
                </p>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function FundsPage() {
  const queryClient = useQueryClient();
  const [opening, setOpening] = useState(false);
  const [drill, setDrill] = useState(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['funds'],
    queryFn: () => call(BASE),
  });

  const setActive = useMutation({
    mutationFn: ({ code, active }) => call(`${BASE}/${code}`, {
      method: 'PATCH', body: JSON.stringify({ is_active: active }),
    }),
    onSuccess: (_d, { active }) => {
      toast.success(active ? 'Fund reopened' : 'Fund closed');
      queryClient.invalidateQueries({ queryKey: ['funds'] });
    },
    onError: (e) => toast.error(e.message, {
      description: 'A fund still holding money keeps its restriction whether or '
        + 'not the ledger shows it, and two funds cannot claim one category.',
    }),
  });

  const funds = data?.funds || [];
  const protectedFunds = funds.filter((f) => f.restriction !== 'unrestricted');

  return (
    <div className="min-h-screen bg-slate-50" data-testid="funds-page">
      <div className="mx-auto max-w-5xl px-6 py-10">
        <header className="mb-10 flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <a href="/admin/finance" className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 transition-colors hover:text-slate-900" data-testid="back-to-finance">
              <ArrowLeft className="h-3 w-3" /> Finance
            </a>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-slate-400">
              <PiggyBank className="h-3.5 w-3.5" /> Fund accounting
            </div>
            <h1 className="mt-2 text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl">
              Whose money is it
            </h1>
            <p className="mt-4 text-base text-slate-600">
              A gift given for the building is not available for the electricity bill, even
              though both sit in the same account. Every figure here comes from the same
              journal entries as the trial balance, so a fund balance cannot drift from the books.
            </p>
          </div>
          <Button onClick={() => setOpening(true)} data-testid="open-fund-btn">
            <Plus className="mr-1 h-4 w-4" /> Open a fund
          </Button>
        </header>

        {isLoading && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-4" data-testid="funds-error">
            <p className="text-sm font-medium text-rose-800">We couldn&apos;t load the funds.</p>
            <p className="mt-1 text-sm text-rose-700">{error.message}</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}
                    data-testid="funds-retry">
              Try again
            </Button>
          </div>
        )}

        {data && (
          <>
            <div className="mb-8 grid gap-4 sm:grid-cols-3" data-testid="fund-position">
              <Card>
                <CardContent className="pt-6">
                  <p className="text-xs uppercase tracking-wider text-slate-400">In cash and bank</p>
                  <p className="mt-1 font-mono text-2xl text-slate-900" data-testid="cash-held">
                    {money(data.cash_and_bank)}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-rose-200">
                <CardContent className="pt-6">
                  <p className="text-xs uppercase tracking-wider text-rose-500">
                    Promised to a purpose
                  </p>
                  <p className="mt-1 font-mono text-2xl text-rose-700" data-testid="restricted-total">
                    {money(data.restricted_total)}
                  </p>
                </CardContent>
              </Card>
              <Card className={data.overcommitted ? 'border-rose-400 bg-rose-50' : 'border-emerald-200 bg-emerald-50'}>
                <CardContent className="pt-6">
                  <p className="text-xs uppercase tracking-wider text-emerald-700">
                    Free to spend
                  </p>
                  <p className="mt-1 font-mono text-2xl text-emerald-800"
                     data-testid="unrestricted-available">
                    {money(data.unrestricted_available)}
                  </p>
                </CardContent>
              </Card>
            </div>

            {data.overcommitted && (
              <div className="mb-8 flex items-start gap-2 rounded-lg border border-rose-300 bg-rose-50 p-4"
                   data-testid="overcommitted-warning">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
                <p className="text-sm text-rose-800">
                  Restricted funds are owed more than the accounts hold. Money given for a
                  purpose has already been spent on something else — it is shown rather
                  than netted away, because it has to be put back.
                </p>
              </div>
            )}

            {protectedFunds.length > 0 && (
              <p className="mb-4 flex items-center gap-2 text-sm text-slate-600">
                <ShieldCheck className="h-4 w-4 text-emerald-600" />
                A payment citing one of these funds is refused if it is outside the fund&apos;s
                purpose or more than the fund holds. General spending is refused if it
                would eat into these balances.
              </p>
            )}

            <div className="space-y-3">
              {funds.map((fund) => {
                const copy = RESTRICTION_COPY[fund.restriction] || RESTRICTION_COPY.restricted;
                return (
                  <Card key={fund.code} className={fund.is_active ? '' : 'opacity-60'}
                        data-testid={`fund-card-${fund.code}`}>
                    <CardHeader className="pb-3">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <CardTitle className="flex flex-wrap items-center gap-2 text-lg">
                            {fund.name}
                            <Badge variant="outline" className={copy.tone}>{copy.label}</Badge>
                            {fund.unregistered && (
                              <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700">
                                No fund record
                              </Badge>
                            )}
                            {!fund.is_active && !fund.unregistered && (
                              <Badge variant="outline">Closed</Badge>
                            )}
                          </CardTitle>
                          <CardDescription>
                            {fund.purpose || (fund.unregistered
                              ? 'Entries carry this fund code but no fund was ever opened for it.'
                              : 'No purpose recorded.')}
                          </CardDescription>
                          {fund.categories.length > 0 && (
                            <p className="mt-2 text-xs text-slate-500">
                              {fund.is_active
                                ? `Receives: ${fund.categories.join(', ')}`
                                : `Received while open: ${fund.categories.join(', ')} — nothing is routed here now`}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-xl text-slate-900"
                             data-testid={`fund-balance-${fund.code}`}>
                            {money(fund.balance)}
                          </p>
                          <p className="text-xs text-slate-500">
                            {money(fund.received)} in · {money(fund.spent)} spent
                          </p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="flex flex-wrap items-center gap-2 pt-0">
                      <Button variant="outline" size="sm" onClick={() => setDrill(fund.code)}
                              data-testid={`fund-drill-${fund.code}`}>
                        {fund.movements} movement{fund.movements === 1 ? '' : 's'}
                        <ArrowRight className="ml-1 h-3 w-3" />
                      </Button>
                      {!fund.unregistered && (fund.is_active ? (
                        <Button variant="ghost" size="sm" className="text-slate-500"
                                onClick={() => setActive.mutate({ code: fund.code, active: false })}
                                disabled={setActive.isPending}
                                data-testid={`fund-close-${fund.code}`}>
                          <Lock className="mr-1 h-3 w-3" /> Close
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm" className="text-slate-500"
                                onClick={() => setActive.mutate({ code: fund.code, active: true })}
                                disabled={setActive.isPending}
                                data-testid={`fund-reopen-${fund.code}`}>
                          <Unlock className="mr-1 h-3 w-3" /> Reopen
                        </Button>
                      ))}
                    </CardContent>
                  </Card>
                );
              })}
              {funds.length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center" data-testid="funds-empty">
                    <p className="text-sm text-slate-600">
                      No funds yet, so all income is treated as unrestricted.
                    </p>
                    <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
                      Open one for anything given for a named purpose — a building, a
                      mission, a bursary — and gifts in its categories will be kept
                      separate from the day the fund exists.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </>
        )}

        <OpenFundDialog open={opening} onClose={() => setOpening(false)} />
        {drill && <FundDrillDown code={drill} onClose={() => setDrill(null)} />}
      </div>
    </div>
  );
}
